import React, { useState, useEffect } from 'react';
import MainLayout from "./MainLayout";
import Apiurl from '../../Helpers/apiUrl';
import Swal from 'sweetalert2'; // Make sure to install: npm install sweetalert2

const Currency = () => {
    const token = localStorage.getItem('u_token');

    const [currencies, setCurrencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [modalMode, setModalMode] = useState('');
    const [currentCurrency, setCurrentCurrency] = useState({ id: '', code: '', symbol: '', exchange_rate: '' });
    const [submitting, setSubmitting] = useState(false);

    const fetchCurrencies = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${Apiurl}admin/currency/list`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();

            if (response.ok) {
                setCurrencies(data.currencies || data.data || data);
            } else {
                setError(data.message || 'Failed to fetch currencies.');
            }
        } catch (err) {
            console.error('Error fetching currencies:', err);
            setError('Something went wrong while connecting to the server.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCurrencies();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const isEdit = modalMode === 'edit';
        const endpoint = isEdit ? `${Apiurl}admin/currency/update` : `${Apiurl}admin/currency/add`;

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(currentCurrency)
            });

            const result = await response.json();

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: result.message || (isEdit ? 'Currency updated successfully!' : 'Currency added successfully!'),
                    timer: 1500,
                    showConfirmButton: false
                });
                setModalMode('');
                fetchCurrencies();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Operation Failed',
                    text: result.message || 'Something went wrong.'
                });
            }
        } catch (err) {
            console.error('Submit error:', err);
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: err
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle Delete with GET Method
    const handleDelete = async (item) => {
        const confirmResult = await Swal.fire({
            title: `Delete ${item.code}?`,
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#111827',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (confirmResult.isConfirmed) {
            try {
                const response = await fetch(`${Apiurl}admin/currency/delete/${item.id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                const result = await response.json();

                if (response.ok) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: result.message || 'Currency deleted successfully.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    fetchCurrencies();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: result.message || 'Failed to delete currency.'
                    });
                }
            } catch (err) {
                console.error('Delete error:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Network error while deleting.'
                });
            }
        }
    };

    const handleOpenAdd = () => {
        setCurrentCurrency({ id: '', code: '', symbol: '', exchange_rate: '' });
        setModalMode('add');
    };

    const handleOpenEdit = (item) => {
        setCurrentCurrency({
            id: item.id,
            code: item.code || '',
            symbol: item.symbol || '',
            exchange_rate: item.exchange_rate || ''
        });
        setModalMode('edit');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: '2-digit'
        });
    };

    return (
        <MainLayout title={'Currency'}>
            <div className="p-4 mx-auto text-xs">
                {/* Header Top */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-2xs gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-base font-bold text-gray-900 tracking-tight">Currencies</h1>
                            <span className="bg-gray-100 text-gray-600 font-medium px-2 py-0.5 rounded-full text-[10px]">
                                {currencies.length} Active
                            </span>
                        </div>
                        <p className="text-gray-500 mt-0.5">Manage exchange rates, codes, and currency symbols across the platform.</p>
                    </div>
                    <button
                        onClick={handleOpenAdd}
                        className="bg-gray-900 text-white font-medium px-3.5 py-2 rounded-md hover:bg-gray-800 transition shadow-2xs flex items-center gap-1.5 shrink-0"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Currency
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-lg mb-3 font-medium">
                        {error}
                    </div>
                )}

                {/* Loading */}
                {loading ? (
                    <div className="text-center py-12 text-gray-400 font-medium">Loading currencies...</div>
                ) : (
                    /* Compact Grid Container */
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {currencies.length > 0 ? (
                            currencies.map((item, index) => (
                                <div
                                    key={item.id || index}
                                    className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col justify-between shadow-2xs hover:border-gray-400 transition"
                                >
                                    <div>
                                        <div className="flex justify-between items-center mb-1.5">
                                            <span className="font-bold text-gray-900 text-sm tracking-wide">{item.code}</span>
                                            <span className="font-mono text-gray-700 bg-gray-100 px-2 py-0.5 rounded font-semibold text-xs border border-gray-200">{item.symbol}</span>
                                        </div>
                                        <div className="text-gray-500 mb-3 bg-gray-50/50 p-1.5 rounded border border-gray-100 flex justify-between items-center">
                                            <span>Rate:</span>
                                            <span className="font-bold text-gray-900 font-mono">{item.exchange_rate}</span>
                                        </div>
                                    </div>

                                    <div className="pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
                                        <span title="Updated Date">Upd: {formatDate(item.updated_at)}</span>
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => handleOpenEdit(item)}
                                                className="text-gray-700 hover:text-gray-900 font-semibold bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded transition"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item)}
                                                className="text-red-600 hover:text-red-800 font-semibold bg-red-50 hover:bg-red-100 p-1 rounded transition"
                                                title="Delete"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full py-12 text-center text-gray-400 font-medium bg-white border border-gray-200 rounded-lg">
                                No currencies found.
                            </div>
                        )}
                    </div>
                )}

                {/* Styled Professional Modal Form */}
                {modalMode && (
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-3xs flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-sm shadow-xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                            {/* Modal Header */}
                            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                <h3 className="font-bold text-gray-900">
                                    {modalMode === 'edit' ? 'Update Currency Details' : 'Create New Currency'}
                                </h3>
                                <button
                                    onClick={() => setModalMode('')}
                                    className="text-gray-400 hover:text-gray-700 font-bold p-1 rounded hover:bg-gray-200/60 transition"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Modal Body */}
                            <form onSubmit={handleSubmit} className="p-4 space-y-3.5">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Currency Code</label>
                                    <input
                                        type="text"
                                        required
                                        value={currentCurrency.code}
                                        onChange={(e) => setCurrentCurrency({ ...currentCurrency, code: e.target.value.toUpperCase() })}
                                        placeholder="e.g. USD, EUR, INR"
                                        className="w-full bg-gray-50/50 border border-gray-300 rounded-md px-3 py-2 text-xs text-gray-900 font-medium focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition"
                                    />
                                    <p className="text-[10px] text-gray-400 mt-0.5">Use standard 3-letter ISO codes.</p>
                                </div>

                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Symbol</label>
                                    <input
                                        type="text"
                                        required
                                        value={currentCurrency.symbol}
                                        onChange={(e) => setCurrentCurrency({ ...currentCurrency, symbol: e.target.value })}
                                        placeholder="e.g. $, €, ₹"
                                        className="w-full bg-gray-50/50 border border-gray-300 rounded-md px-3 py-2 text-xs text-gray-900 font-medium focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Exchange Rate</label>
                                    <input
                                        type="number"
                                        step="any"
                                        required
                                        value={currentCurrency.exchange_rate}
                                        onChange={(e) => setCurrentCurrency({ ...currentCurrency, exchange_rate: e.target.value })}
                                        placeholder="e.g. 1.00"
                                        className="w-full bg-gray-50/50 border border-gray-300 rounded-md px-3 py-2 text-xs text-gray-900 font-medium focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 transition"
                                    />
                                    <p className="text-[10px] text-gray-400 mt-0.5">Rate relative to your base currency.</p>
                                </div>

                                <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setModalMode('')}
                                        className="px-3.5 py-1.5 rounded-md font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="px-3.5 py-1.5 rounded-md font-medium text-white bg-gray-900 hover:bg-gray-800 transition disabled:opacity-50 shadow-2xs"
                                    >
                                        {submitting ? 'Saving...' : (modalMode === 'edit' ? 'Update Currency' : 'Save Currency')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </MainLayout>
    );
};

export default Currency;